import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";

import { AppDispatch, RootState } from "store";
import {
  fetchCreditBalance,
  fetchPaymentHistory,
  purchaseCredits,
  checkStripeConnection,
} from "store/paymentSlice";
import PageSideBar from "components/PageSideBar";
import PaymentHistory from "components/PaymentHistory";
import ConfirmationDialog from "components/ConfirmationDialog";
import FinishDialog from "components/FinishDialog";
import CreditPurchaseView from "./components/CreditPurchaseView";
import { AppRoutes } from "pages/constants/routes";

import styles from "./Credit.module.scss";
import { ReactComponent as MissingPaymentIcon } from "assets/misc/missing_payment.svg";

const Credit: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { creditBalance, paymentHistory, error, isStripeConnected } =
    useSelector((state: RootState) => state.payment);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Combined state for FinishDialog (both success and error cases)
  const [finishDialog, setFinishDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    warning: false,
  });

  useEffect(() => {
    const initializeData = async () => {
      await Promise.all([
        dispatch(checkStripeConnection()),
        dispatch(fetchPaymentHistory()),
      ]);
    };
    initializeData();
  }, [dispatch]);

  const handlePaymentVerification = useCallback(async () => {
    try {
      setIsProcessing(true);
      await Promise.all([
        dispatch(fetchCreditBalance()),
        dispatch(fetchPaymentHistory()),
      ]);
      setFinishDialog({
        isOpen: true,
        title: "Payment Successful",
        message: "Your credits have been added to your account.",
        warning: false,
      });
    } catch (error) {
      setFinishDialog({
        isOpen: true,
        title: "Payment Verification Failed",
        message: "Failed to verify payment. Please contact support.",
        warning: true,
      });
    } finally {
      setIsProcessing(false);
    }
  }, [dispatch]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get("session_id");

    if (sessionId) {
      handlePaymentVerification();
      navigate(AppRoutes.credit, { replace: true });
    }
  }, [handlePaymentVerification, location, navigate]);

  const handlePurchaseConfirm = (amount: number) => {
    if (!isStripeConnected) {
      setFinishDialog({
        isOpen: true,
        title: "Connection Required",
        message: "Please connect your Stripe account first",
        warning: true,
      });
      return;
    }
    setPurchaseAmount(amount);
    setShowConfirmation(true);
  };

  const handlePurchasePayment = async () => {
    if (!isStripeConnected) {
      setFinishDialog({
        isOpen: true,
        title: "Connection Required",
        message: "Please connect your Stripe account first",
        warning: true,
      });
      return;
    }

    try {
      setIsProcessing(true);
      setShowConfirmation(false);
      await dispatch(purchaseCredits(purchaseAmount)).unwrap();
      // Redirect will happen automatically after this
    } catch (error) {
      setFinishDialog({
        isOpen: true,
        title: "Payment Failed",
        message:
          error instanceof Error
            ? error.message
            : "Payment failed. Please try again.",
        warning: true,
      });
      setIsProcessing(false);
    }
  };

  const renderNoPaymentMethod = () => (
    <div className={styles.content}>
      <div className={styles.header}>
        <h2>Get Credits</h2>
        <div className={styles.balance}>
          Current Credits: {creditBalance.toFixed(2)}
        </div>
      </div>
      <div className={styles.noPaymentBox}>
        <MissingPaymentIcon />
        <div className={styles.noPaymentContent}>
          <div className={styles.title}>No payment method found</div>
          <div className={styles.subtitle}>
            Connect to a payment method to get started
          </div>
        </div>
        <Link className={styles.connectButton} to={AppRoutes.settings}>
          Go to Settings
        </Link>
      </div>
    </div>
  );

  const renderPurchaseCredit = () => (
    <div className={styles.content}>
      <div className={styles.header}>
        <h2>Get Credits</h2>
        <div className={styles.balance}>
          Current Credits: {creditBalance.toFixed(2)}
        </div>
      </div>
      <div className={styles.mainContent}>
        <CreditPurchaseView
          onPurchase={handlePurchaseConfirm}
          isProcessing={isProcessing}
        />
        <div className={styles.verticalSeparator} />
        <PaymentHistory
          history={paymentHistory}
          isStripeConnected={isStripeConnected}
          onSetupStripe={() => navigate("/payment")}
          error={error || undefined}
        />
      </div>

      {showConfirmation && (
        <ConfirmationDialog
          message={`You are about to purchase ${purchaseAmount} credits for SGD ${purchaseAmount}`}
          onConfirm={handlePurchasePayment}
          onCancel={() => setShowConfirmation(false)}
        />
      )}

      <FinishDialog
        isOpen={finishDialog.isOpen}
        setIsOpen={(isOpen) => setFinishDialog((prev) => ({ ...prev, isOpen }))}
        title={finishDialog.title}
        message={finishDialog.message}
        warning={finishDialog.warning}
        confirmButtonLabel="OK"
      />
    </div>
  );

  return (
    <div className={styles.page}>
      <PageSideBar />
      {!isStripeConnected ? renderNoPaymentMethod() : renderPurchaseCredit()}
    </div>
  );
};

export default Credit;
